import { useState, useMemo, useCallback, useEffect } from "react"
import { Tag } from "antd"
import { CloseCircleOutlined } from "@ant-design/icons"
import styles from "../index.module.css"

/**
 * Custom hook for managing equipment characteristics
 * Handles both backend characteristics and user-created tags
 *
 * @param {Array} characteristicListing - Backend characteristic listing from Redux
 * @param {Object} characteristicIdToLabel - Map of characteristic IDs to labels
 * @returns {Object} - Characteristics state and handlers
 */
export const useEquipmentCharacteristics = (characteristicListing, characteristicIdToLabel) => {
  // Selected characteristics now stored as array of objects: { id: characteristics_to_boat_id, characteristicId: join record id (boat characteristic id), name }
  const [selected, setSelected] = useState([]) // [{ id, characteristicId, name }]
  const [removedCharacteristics, setRemovedCharacteristics] = useState([]) // deselected existing join records to destroy
  // Track user-created characteristics (new tags entered by user)
  const [userCreatedCharacteristics, setUserCreatedCharacteristics] = useState([]) // [{ id, name }]

  // Clean up user-created characteristics that match backend characteristics by name
  // This prevents duplicate entries when backend data loads after user creates a tag
  useEffect(() => {
    if (characteristicListing.length > 0 && userCreatedCharacteristics.length > 0) {
      const backendNames = new Set(
        characteristicListing.map(c => String(c.name).toLowerCase().trim())
      )
      setUserCreatedCharacteristics(prev =>
        prev.filter(uc => !backendNames.has(String(uc.name).toLowerCase().trim()))
      )
    }
  }, [characteristicListing])

  // Pre-computed options array for Select - combines backend listing with user-created tags and selected items
  const characteristicOptions = useMemo(() => {
    // Deduplicate characteristicListing first to handle duplicate API data
    const uniqueListing = characteristicListing.filter((item, index, self) =>
      index === self.findIndex((c) => String(c.id) === String(item.id))
    )

    const backendOptions = uniqueListing.map((c) => {
      const id = String(c.id)
      return { value: id, label: characteristicIdToLabel[id] || c.name || `Characteristic ${c.id}` }
    })

    // Add selected characteristics that aren't in backend listing (happens when editing before backend loads)
    const selectedOptions = selected
      .filter((s) => {
        const selectedId = String(s.id)
        return !uniqueListing.some((c) => String(c.id) === selectedId)
      })
      .filter((s) => s.name) // Only include if has a name
      .map((s) => ({ value: String(s.id), label: s.name }))

    // Add user-created characteristics that aren't already in backend listing or selected
    const userCreatedOptions = userCreatedCharacteristics
      .filter((uc) => {
        const ucId = String(uc.id)
        return !uniqueListing.some((c) => String(c.id) === ucId) &&
          !selected.some((s) => String(s.id) === ucId)
      })
      .map((uc) => ({ value: String(uc.id), label: uc.name }))

    const allOptions = [...backendOptions, ...selectedOptions, ...userCreatedOptions]

    // Final deduplication by BOTH value AND label to prevent any duplicate entries
    // This handles cases where same label has different IDs (user-created "a3" vs backend id:5 "a3")
    const deduplicatedOptions = allOptions.filter((option, index, self) =>
      index === self.findIndex((o) =>
        String(o.value) === String(option.value) ||
        (o.label && option.label && String(o.label).toLowerCase().trim() === String(option.label).toLowerCase().trim())
      )
    )

    return deduplicatedOptions
  }, [characteristicListing, characteristicIdToLabel, userCreatedCharacteristics, selected])

  // Derived array of selected ids for feeding into antd Select value
  // Deduplicate to prevent duplicate tags in the Select component
  const selectedIds = useMemo(() => {
    const ids = selected.map((c) => c.id)
    return [...new Set(ids)]
  }, [selected])

  // Custom tag render so selected chips show label instead of raw id
  const renderCharacteristicTag = useCallback(
    (tagProps) => {
      const { value, onClose, closable, label } = tagProps
      // Priority: selected name > option label > user-created > fallback to ID
      const selectedItem = selected.find((s) => String(s.id) === String(value))
      const userCreatedLabel = userCreatedCharacteristics.find((uc) => String(uc.id) === String(value))?.name
      const resolvedLabel = selectedItem?.name || label || userCreatedLabel || String(value)
      return (
        <Tag
          color="blue"
          closable={closable}
          onClose={onClose}
          closeIcon={<CloseCircleOutlined />}
          className={`mr-xxs ${styles.selectedTag}`}
        >
          {resolvedLabel}
        </Tag>
      )
    },
    [characteristicIdToLabel, userCreatedCharacteristics, selected]
  )

  // Handler for Select characteristics change
  const handleCharacteristicsChange = useCallback(
    (ids, optionObjs, form) => {
      // Deduplicate IDs to prevent duplicate tags
      const uniqueIds = [...new Set(ids.map(String))]

      const optsArray = Array.isArray(optionObjs) ? optionObjs : [optionObjs]
      const idToLabel = {}
      for (const o of optsArray) {
        if (o?.value) idToLabel[String(o.value)] = o.label || o.children || characteristicIdToLabel[String(o.value)]
      }

      // Detect new tags created by user (not in characteristicListing or userCreatedCharacteristics)
      const newUserCreatedTags = []
      for (const id of uniqueIds) {
        const idStr = String(id)
        const isInBackendListing = characteristicListing.some((c) => String(c.id) === idStr)
        const isInUserCreated = userCreatedCharacteristics.some((uc) => String(uc.id) === idStr)

        if (!isInBackendListing && !isInUserCreated) {
          // This is a new user-created tag
          newUserCreatedTags.push({
            id: idStr,
            name: idToLabel[idStr] || idStr
          })
        }
      }

      // Add new user-created tags to state
      if (newUserCreatedTags.length > 0) {
        setUserCreatedCharacteristics((prev) => [...prev, ...newUserCreatedTags])
      }

      // Track removed items (comparing previous selected with new uniqueIds)
      const removedIds = selected.filter((s) => !uniqueIds.includes(s.id)).map((s) => s.id)

      // IMPORTANT: Different behavior for user-created vs backend characteristics
      // - User-created: Remove from BOTH input AND dropdown when deleted
      // - Backend: Remove from input only, stay in dropdown for re-selection
      if (removedIds.length > 0) {
        setUserCreatedCharacteristics((prev) =>
          prev.filter((uc) => !removedIds.includes(String(uc.id)))
        )
      }

      // Track removed backend characteristics (ones with characteristicId) for _destroy
      const removedNow = selected.filter((s) => !uniqueIds.includes(s.id) && s.characteristicId)
      setRemovedCharacteristics((prev) => {
        // Add newly removed (avoid duplicates), keep previous ones still not re-added
        const combined = [...prev]
        for (const r of removedNow) {
          if (!combined.some((c) => c.characteristicId === r.characteristicId)) combined.push(r)
        }
        // If user re-added a previously removed item, filter it out
        return combined.filter((r) => !uniqueIds.includes(r.id))
      })

      // Build updated characteristicIdToLabel including user-created tags
      const extendedIdToLabel = { ...characteristicIdToLabel }
      for (const uc of userCreatedCharacteristics) {
        extendedIdToLabel[String(uc.id)] = uc.name
      }
      for (const tag of newUserCreatedTags) {
        extendedIdToLabel[String(tag.id)] = tag.name
      }

      const newSelected = uniqueIds.map((id) => {
        // If re-added an item that was previously removed, restore its characteristicId
        const existing = selected.find((s) => s.id === String(id))
        const wasRemoved = removedCharacteristics.find((r) => r.id === String(id))
        return {
          id: String(id),
          characteristicId: existing?.characteristicId || wasRemoved?.characteristicId || null,
          name: idToLabel[String(id)] || extendedIdToLabel[String(id)] || String(id)
        }
      })

      // Additional deduplication: ensure no duplicate IDs in selected state
      const deduplicatedSelected = newSelected.filter((item, index, self) =>
        index === self.findIndex((t) => t.id === item.id)
      )

      setSelected(deduplicatedSelected)
      if (form) {
        form.setFieldsValue({ characteristics: deduplicatedSelected.map((c) => c.id) })
      }
    },
    [characteristicListing, characteristicIdToLabel, userCreatedCharacteristics, selected, removedCharacteristics]
  )

  return {
    selected,
    setSelected,
    removedCharacteristics,
    userCreatedCharacteristics,
    selectedIds,
    characteristicOptions,
    renderCharacteristicTag,
    handleCharacteristicsChange
  }
}
